import { Button, FormInstance, message } from 'antd';
import { useEffect, useState } from 'react';
import { LOCAL_STORAGE, TIMER } from '../common/constants';
import {
  getItem,
  getNumberAndCode,
  removeItem,
  setItem,
} from '../common/utils';
import { initiate } from '../modules/auth/OtpLess';
import './style.less';

type ResendOtpProps = {
  phoneNumber: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  verifyOtpForm?: FormInstance<any>;
};

const ResendOtp: React.FC<ResendOtpProps> = ({
  phoneNumber,
  verifyOtpForm,
}) => {
  const [timer, setTimer] = useState<number>(0);
  const [attempts, setAttempts] = useState<number>(0);
  const [blocked, setBlocked] = useState<boolean>(false);

  useEffect(() => {
    const storedAttempts = parseInt(
      getItem(LOCAL_STORAGE.OTP_ATTEMPTS) || '0',
      10,
    );
    const storedStartTime = parseInt(
      getItem(LOCAL_STORAGE.OTP_START_TIME) || '0',
      10,
    );
    const storedBlockTime = parseInt(
      getItem(LOCAL_STORAGE.OTP_BLOCK_TIME) || '0',
      10,
    );
    const currentTime = Date.now();

    if (storedBlockTime && currentTime < storedBlockTime) {
      setBlocked(true);
      setTimer(Math.ceil((storedBlockTime - currentTime) / 1000));
      return;
    }

    let remainingTime = 0;
    if (storedStartTime) {
      const elapsedTime = Math.floor((currentTime - storedStartTime) / 1000);

      if (storedAttempts === 1) {
        remainingTime = Math.max(
          TIMER.SECOND_TIME_RESEND_OTP_SECONDS - elapsedTime,
          0,
        );
      } else if (storedAttempts >= 2) {
        remainingTime = Math.max(
          TIMER.THIRD_TIME_RESEND_OTP_SECONDS - elapsedTime,
          0,
        );
      } else {
        remainingTime = Math.max(TIMER.INITIAL_TIME_SECONDS - elapsedTime, 0);
      }
    }

    if (remainingTime === 0) {
      removeItem(LOCAL_STORAGE.OTP_START_TIME);
    }

    setTimer(remainingTime);
    setAttempts(storedAttempts);
  }, []);

  useEffect(() => {
    const storedTimer = parseInt(getItem(LOCAL_STORAGE.OTP_TIMER) || '0', 10);
    if (storedTimer > 0) {
      setTimer(storedTimer);
    }
  }, []);

  useEffect(() => {
    if (!blocked && timer > 0) {
      const countdown = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            clearInterval(countdown);
            removeItem(LOCAL_STORAGE.OTP_START_TIME);
            removeItem(LOCAL_STORAGE.OTP_TIMER);
            return 0;
          }
          const newTimer = prev - 1;
          setItem(LOCAL_STORAGE.OTP_TIMER, newTimer.toString());
          return newTimer;
        });
      }, 1000);

      return () => clearInterval(countdown);
    }
  }, [timer, blocked]);

  const handleResendOtp = () => {
    if (blocked) return;
    verifyOtpForm?.setFieldValue('otp', '');
    if (attempts >= TIMER.MAX_OTP_ATTEMPTS) {
      setBlocked(true);
      const blockEndTime = Date.now() + TIMER.BLOCK_DURATION_SECONDS * 1000;
      setItem(LOCAL_STORAGE.OTP_BLOCK_TIME, blockEndTime.toString());
      setTimer(TIMER.BLOCK_DURATION_SECONDS);
      return;
    }

    const sanitizedNumber = phoneNumber.replace(/\D/g, '');
    const data = getNumberAndCode(sanitizedNumber);

    if (data?.nationalNumber && data?.countryCallingCode) {
      initiate(
        data.nationalNumber,
        data.countryCallingCode,
        TIMER.BLOCK_DURATION_SECONDS,
      );
      message.success('OTP Resent Successfully!');

      const newAttempts = attempts + 1;
      setAttempts(newAttempts);
      setItem(LOCAL_STORAGE.OTP_ATTEMPTS, newAttempts.toString());

      let newTimer = TIMER.INITIAL_TIME_SECONDS;
      if (newAttempts === 1) {
        newTimer = TIMER.SECOND_TIME_RESEND_OTP_SECONDS;
      } else if (newAttempts >= 2) {
        newTimer = TIMER.THIRD_TIME_RESEND_OTP_SECONDS;
      }

      setTimer(newTimer);
      setItem(LOCAL_STORAGE.OTP_START_TIME, Date.now().toString());
    }
  };

  return (
    <Button
      className="resend-otp-btn"
      type="text"
      disabled={blocked || timer > 0}
      onClick={handleResendOtp}
    >
      {blocked
        ? `Too many attempts! Try again in 10 minutes`
        : timer > 0
          ? `Resend OTP in ${timer} seconds`
          : 'Resend OTP'}
    </Button>
  );
};

export default ResendOtp;
